/**
 * The guest app's theme: a three-way preference resolved to one of two grounds.
 *
 * WHY `system` IS THE DEFAULT. Every other surface in this product is a staff
 * app on a venue-owned device, where a fixed theme is fine. This one is opened
 * by a stranger, on their own phone, at a table, usually in a dim room, and the
 * failure mode is a full-brightness white screen across a dinner table. The
 * phone already knows whether it is in dark mode, so the app follows it unless
 * the guest says otherwise, with no first-run flash of the wrong ground (see
 * the inline script in `index.html`).
 */

export type ThemePreference = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

/** Shared with the pre-paint script in `index.html`; keep the two in step. */
export const THEME_STORAGE_KEY = 'venue.theme';

export const THEME_PREFERENCES: readonly ThemePreference[] = ['system', 'light', 'dark'] as const;

/**
 * The page ground of each theme, mirroring `--background` in `index.css`.
 *
 * Duplicated here on purpose: `<meta name="theme-color">` colours the browser
 * and the standalone-PWA status bar, and that is set from JS, not from CSS. A
 * mismatch is visible as a seam along the top of the screen.
 */
export const THEME_GROUND: Record<ResolvedTheme, string> = {
  light: '#FAF7F1',
  dark: '#080604',
};

export const DARK_MEDIA_QUERY = '(prefers-color-scheme: dark)';

const isPreference = (value: unknown): value is ThemePreference =>
  value === 'light' || value === 'dark' || value === 'system';

/**
 * Anything unrecognised — a first visit, a cleared store, or a value written by
 * an older build — means "follow the phone".
 */
export function readStoredTheme(): ThemePreference {
  try {
    const raw = localStorage.getItem(THEME_STORAGE_KEY);
    return isPreference(raw) ? raw : 'system';
  } catch {
    // Private browsing can throw on access, not just on write.
    return 'system';
  }
}

export function storeTheme(preference: ThemePreference): void {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, preference);
  } catch {
    // Persistence is a convenience; the choice still applies for this visit.
  }
}

/** `false` when the browser has no opinion, which is what a light default means. */
export function systemPrefersDark(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia(DARK_MEDIA_QUERY).matches
    : false;
}

export function resolveTheme(preference: ThemePreference, prefersDark: boolean): ResolvedTheme {
  if (preference === 'system') return prefersDark ? 'dark' : 'light';
  return preference;
}

/**
 * Put the resolved theme on the document.
 *
 * `color-scheme` comes from the CSS, so the only things left are the class the
 * `dark:` variant keys off and the status-bar colour.
 */
export function applyTheme(resolved: ResolvedTheme): void {
  document.documentElement.classList.toggle('dark', resolved === 'dark');

  let meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]:not([media])');
  if (!meta) {
    meta = document.createElement('meta');
    meta.name = 'theme-color';
    document.head.appendChild(meta);
  }
  meta.content = THEME_GROUND[resolved];
}
